import { DetectedWebView, detectWebView } from "@ciaopeople/cp-shatsu/ClientDetection";
import { NullAdsController } from "@mp-commons/modules/ads/AdsController.js";
import { CP_AdvSiteName, CP_EmbedType } from "@mp-commons/modules/ads/adcall/CustomParams.js";
import { ContentSpecificAdData, ExternallyPassedContentAdData } from "@mp-commons/modules/ads/adcall/composable/ContentSpecificAdCallFilter.js";
import { UserAdsData } from "@mp-commons/modules/ads/adcall/composable/UserAdsData.js";
import { MultipleVASTImaAdCallController, MultipleVASTImaAdCallControllerConfig, VASTAdUnitConfig } from "@mp-commons/modules/ads/ima/MultipleVASTImaAdCallController.js";
import { BaseUnmuter, NoOpUnumuter } from "@mp-commons/modules/unmuter/UserGestureUnmuter.js";;
import { IMAAdvancedUnmuter, IMAUnmuterConservativeConfig, IMAUnmuterAnyConfig } from "@mp-commons/modules/unmuter/IMAUserGestureUnmuter.js";;
import { NullPrebidModule } from "@mp-commons/modules/ads/prebid/NullPrebidModule.js";
import { VideoPolicyRules } from "@mp-commons/modules/ads/vprules/VideoPolicyRules.js";
import { MediaState, MediaStatusSubscription } from "@mp-commons/modules/player/AbstractPlayerControls.js";
import { createSizePreference } from "@mp-commons/modules/player/video/FormatAndSizePreference.js";
import { ExternalCloseUIElement, HasExternalUIElements, VideoPlayerControls } from "@mp-commons/modules/player/Player.js";
import { PreloadManagementStrategy, UnloadManagementStrategy } from "@mp-commons/modules/player/PlayerControlsConfig.js";
import { AudioMode, PlayMode } from "@mp-commons/modules/player/MediaPlayOptions.js";
import { NativeVODLoadStrategy, NativeVODPlayerFactory } from "@mp-commons/modules/player/video/native/NativeVODPlayerFactory.js";
import { StaticPlaylist } from "@mp-commons/modules/playlist/StaticPlaylist.js";
import { dispatchMegarideEvents } from "../MegarideEventDispatch.js";
import { MegaridePlayerControls, ParthenopePlayer } from "../MegaridePlayer.js";
import { ComscoreLoadMode, ParthenopePlayerConfig } from "../MegaridePlayerConfig.js";
import { MegaridePlayerControlsBaseImpl, MegaridePlayerControlsConfig } from "../MegaridePlayerControlsBaseImpl.js";
import { getAdvConfiguration } from "../ads/AdvConfig.js";
import { fetchABRStreamingEngine } from "../modules/ABRStreamingEngineModule.js";
import { MegarideCSStreamingTagMapper } from "../stats/mappers/MegarideCSStreamingTagMapper.js";
import { populateAuthorPlaceholder, populateViewsPlaceholder } from "../utils/YMutils.js";
import { ImaControllerBuilder } from "./builders/ImaControllerBuidler.js";
import { PrebidBuilder } from "./builders/PrebidBuilder.js";
import { ResolverBuilder } from "./builders/ResolverBuilder.js";
import { StatsMapperBuilder } from "./builders/StatsMapperBuilder.js";
import { ParthenopeGUIV2 } from "./gui/ParthenopeGUIV2.js";
import { PreloadReleaseMode } from "@mp-commons/modules/player/PreloadReleaseMode.js";
import { PlayerTechFactoryConfig } from "@mp-commons/modules/player/PlayerTechFactory.js";
import { VideoPlaylist } from "@mp-commons/model/video/VideoPlaylist.js";
import { VideoContainerType } from "@mp-commons/model/video/VideoFormat.js";
import { VideoInfoRawData } from "@mp-commons/model/video/VideoInfo.js";
import { MediaAudioOnPolicy, MediaAutoplayPolicy } from "@mp-commons/config/MediaPlayerConfig.js";
import { AmazonBidBehaviour } from "@mp-commons/modules/ads/prebid/AmazonBidder.js";
import { buildVastAdUnitsConfig } from "../ads/AdUnitsBuilder.js";

const PLAYERNAME = "ParthenopePlayerV3";



/**
 * Represents the third version of the Parthenope media player implementation.
 * 
 * This class provides static factory methods for building and initializing player instances
 * with different streaming and management strategies, including ABR (Adaptive Bitrate) streaming,
 * native VOD management, and advertising support. It encapsulates player controls, GUI management,
 * configuration, and event dispatching for the Parthenope player.
 * 
 * @remarks
 * - Use the static `abrStreamingMgmtBuild`, `managementBuild`, or `build` methods to create an instance.
 * - Supports integration with advertising modules, analytics, and custom configuration.
 * - Handles player initialization, playback control, GUI updates, and external UI elements.
 * 
 * @example
 * ```typescript
 * const player = await ParthenopePlayerV3.build(playlist, config, extData, rootElement, window);
 * await player.init();
 * player.start();
 * ```
 * 
 * @see ParthenopePlayer
 * @see ParthenopeGUIV2
 * @see MegaridePlayerControls
 */
export class ParthenopePlayerV3 implements ParthenopePlayer {

    private readonly name: string = PLAYERNAME


    /**
     * Builds and initializes an ABR (Adaptive Bitrate) streaming player instance for the ParthenopePlayerV3.
     *
     * @param playlist - The video playlist to be played.
     * @param config - The configuration object for the Parthenope player.
     * @param rootElement - The root HTML element where the player UI will be rendered. Optional; defaults to `null`.
     * @param window - The browser window object to be used by the player.
     * @returns A promise that resolves to an instance of `ParthenopePlayerV3` once the player is fully initialized.
     */
    static abrStreamingMgmtBuild(playlist: VideoPlaylist,
        config: ParthenopePlayerConfig,
        rootElement: HTMLElement = null,
        window: Window
    ): Promise<ParthenopePlayerV3> {
        const cPlaylist = new StaticPlaylist<VideoInfoRawData>(playlist);
        const gui = new ParthenopeGUIV2(window, rootElement, config);

        const unmuter = new NoOpUnumuter();

        const playerOptions: MegaridePlayerControlsConfig = {
            events: {
                enable: false,
                dynamic_viewable_impression: true,
                dynamic_viewable_impression_cap: 5,
                progress_resolution: 1
            },
            autoplay: config.embeddablePlayerConfig?.autoplayPolicy || MediaAutoplayPolicy.AUTOPLAY_ALWAYS,
            loopPlaylist: config.embeddablePlayerConfig?.loopPlaylist || false,
            usePreferenceQuality: false,
            audioOnPolicy: MediaAudioOnPolicy.OFF,

        }

        const factoryConfig: PlayerTechFactoryConfig = {
            enableGC: false,
            unloadManagementStrategy: UnloadManagementStrategy.REMOVE_PRELOADED_RESOURCE,
            preloadManagementStrategy: PreloadManagementStrategy.NO_CLEANUP,
            playOptions: {
                audioMode: AudioMode.SELECTABLE_AUDIO,
                playMode: PlayMode.SINGLE_PLAY,
                timeCutSeconds: -1
            },
            enableReusingMediaResource: false,
            preloadReleaseMode: PreloadReleaseMode.RELEASE_ON_PLAYER_READY,
        }


        const adControllerPromise = Promise.resolve(new NullAdsController())

        const controlsPromise = fetchABRStreamingEngine().then(engine => engine(cPlaylist,
            gui,
            playerOptions,
            factoryConfig,
            adControllerPromise,
            unmuter, window));

        return controlsPromise.then(ctrl => {
            return new ParthenopePlayerV3(ctrl, gui, config, window)
        });
    }


    /**
     * Builds and initializes a ParthenopePlayerV3 instance with the provided playlist, configuration, and DOM elements.
     *
     * This static method sets up the video player controls, GUI, playlist, and other dependencies required for the player.
     * It configures playback options, quality preferences, and ad controllers, returning a promise that resolves to a fully
     * constructed ParthenopePlayerV3.
     *
     * @param playlist - The video playlist to be managed by the player.
     * @param config - The configuration object for the Parthenope player.
     * @param rootElement - The root HTML element where the player GUI will be rendered. Defaults to `null`.
     * @param window - The browser window object for DOM and event management.
     * @returns A promise that resolves to an instance of `ParthenopePlayerV3`.
     */
    static managementBuild(playlist: VideoPlaylist,
        config: ParthenopePlayerConfig,
        rootElement: HTMLElement = null,
        window: Window
    ): Promise<ParthenopePlayerV3> {

        const cPlaylist = new StaticPlaylist<VideoInfoRawData>(playlist);
        const gui = new ParthenopeGUIV2(window, rootElement, config);



        const factoryBuilder = (ctrl: VideoPlayerControls) => {
            const factory = new NativeVODPlayerFactory(ctrl, window, {
                enableGC: false,
                preloadReleaseMode: PreloadReleaseMode.RELEASE_ON_PLAYER_READY,
                unloadManagementStrategy: UnloadManagementStrategy.REMOVE_PRELOADED_RESOURCE,
                preloadManagementStrategy: PreloadManagementStrategy.NO_CLEANUP,
                playOptions: {
                    audioMode: AudioMode.SELECTABLE_AUDIO,
                    playMode: PlayMode.SINGLE_PLAY,
                    timeCutSeconds: -1
                }, enableReusingMediaResource: false,
                selectedContainer: VideoContainerType.PROGRESSIVE_MP4,
                loadStrategy: NativeVODLoadStrategy.LAZY_LOAD
            });

            factory.setSizePreferencePolicy(createSizePreference({
                size: 360, //replace with setting from user preferences
                fallback: "lower",
                dimension: "significant"
            }));


            return factory;

        }

        // const factory = new NativeVODPlayerFactory(window, VideoContainerType.PROGRESSIVE_MP4, NativeVODLoadStrategy.LAZY_LOAD);
        // factory.setSizePreferencePolicy(createSizePreference({
        //     size: 360, //replace with setting from user preferences
        //     fallback: "lower",
        //     dimension: "significant"
        // }));


        const unmuter = new NoOpUnumuter();

        const playerOptions: MegaridePlayerControlsConfig = {

            events: {
                enable: false,
                dynamic_viewable_impression: true,
                dynamic_viewable_impression_cap: 5,
                progress_resolution: 1
            },
            autoplay: config.embeddablePlayerConfig?.autoplayPolicy || MediaAutoplayPolicy.AUTOPLAY_IF_ADS_ARE_SERVABLE,
            loopPlaylist: config.embeddablePlayerConfig?.loopPlaylist || false,
            usePreferenceQuality: false,
            audioOnPolicy: MediaAudioOnPolicy.OFF
        }


        const adControllerPromise = Promise.resolve(new NullAdsController())

        const controlsPromise = Promise.resolve(new MegaridePlayerControlsBaseImpl(
            PLAYERNAME, cPlaylist, factoryBuilder, adControllerPromise, gui, playerOptions, unmuter, window))

        return controlsPromise.then(ctrl => {
            return new ParthenopePlayerV3(ctrl, gui, config, window)
        });

    }

    /**
     * Builds and initializes a new instance of `ParthenopePlayerV3` with the provided playlist, ad configuration, player settings, and external data.
     *
     * This method sets up the player environment, including GUI, playlist, ad controllers, statistics mappers, and playback policies.
     * It also handles conditional logic for audio policies, quality preferences, and third-party integrations such as Comscore and Prebid.
     *
     * @param playlist - The video playlist to be played.
     * @param config - Configuration object for the Parthenope player.
     * @param extData - Optional externally passed content/ad data. Defaults to `null`.
     * @param rootElement - Optional root HTML element for player rendering. Defaults to `null`.
     * @param window - The browser window context.
     * @returns A promise that resolves to an initialized `ParthenopePlayerV3` instance.
     */
    static build(playlist: VideoPlaylist,
        config: ParthenopePlayerConfig,
        extData: ExternallyPassedContentAdData = null,
        rootElement: HTMLElement = null,
        window: Window
    ): Promise<ParthenopePlayerV3> {

        const wvDetect: DetectedWebView = detectWebView();
        const contentSpecificAdData = new ContentSpecificAdData(window, extData, null)

        let imaunmuterconfig = IMAUnmuterConservativeConfig;
        // gain effect su fanplayer #13804 + #13990 + #14013
        const gainConditionCheck = !config.embeddablePlayerConfig.withoutAdvertising && (!wvDetect.device?.other && !wvDetect.platform.ios)
            && (extData.embedType == CP_EmbedType.FANPLAYER || extData.embedType == CP_EmbedType.VIDEO_TESTO);

        if (gainConditionCheck) {
            config.embeddablePlayerConfig.audioOnPolicy = MediaAudioOnPolicy.ON_WHEN_AVAILABLE;
        }

        const useComscoreLoadMode: ComscoreLoadMode = config.comscoreLoadMode ? config.comscoreLoadMode : "jit";
        const enableComscore = true;

        const cPlaylist = new StaticPlaylist<VideoInfoRawData>(playlist);
        const gui = new ParthenopeGUIV2(window, rootElement, config);
        const useVideoPolicy = config.embeddablePlayerConfig.videoPolicy ? config.embeddablePlayerConfig.videoPolicy : VideoPolicyRules.GOOGLE_UPDATED_VIDEO_POLICY;

        const unmuter = new BaseUnmuter(gui.rootEl, gui.getVideoElement().mediaItem, { applyGainEffect: gainConditionCheck, gainLevel: 0.01 });
        const userData = new UserAdsData(window);

        const stats = StatsMapperBuilder(window, extData.embedType, {
            ga4: {
                enable: true,
                media: false,
                aderrors: false,
                adevents: false,
                media_progress: false
            },
            ymstats: {
                enable: true,
                media: true,
                media_progress: true,
                adevents: true,
                aderrors: true
            },
            nielsen: {
                enable: true
            },
            comscore: {
                enable: enableComscore,
            },
            playerName: "megaride"
        }, extData)


        let maybeComscorePromise: Promise<void> = Promise.resolve();
        if (enableComscore) {
            console.log("COMSCORE load mode is " + useComscoreLoadMode);
            if (useComscoreLoadMode != "jit") {
                const maybeComscore = stats.find((el) => el instanceof MegarideCSStreamingTagMapper);
                if (maybeComscore && maybeComscore instanceof MegarideCSStreamingTagMapper) {
                    console.log("COMSCORE pre-scheduling module download");
                    const pPromise = maybeComscore.preload();
                    if (useComscoreLoadMode == "preload") {
                        console.log("COMSCORE await module set");
                        maybeComscorePromise = pPromise;
                    }
                }
            }
        }


        const factoryBuilder = (ctrl: VideoPlayerControls) => {
            const factory = new NativeVODPlayerFactory(ctrl, window, {
                enableGC: false,
                preloadReleaseMode: PreloadReleaseMode.RELEASE_ON_PLAYER_READY,
                unloadManagementStrategy: UnloadManagementStrategy.REMOVE_PRELOADED_RESOURCE,
                preloadManagementStrategy: PreloadManagementStrategy.NO_CLEANUP,
                enableReusingMediaResource: false,
                playOptions: {
                    audioMode: AudioMode.SELECTABLE_AUDIO,
                    playMode: PlayMode.SINGLE_PLAY,
                    timeCutSeconds: -1
                },
                selectedContainer: VideoContainerType.PROGRESSIVE_MP4,
                loadStrategy: NativeVODLoadStrategy.LAZY_LOAD
            });
            const useSize: () => number = () => {

                // if (config.embeddablePlayerConfig?.usePreferenceQuality) {
                //     const size_preference = localStorage.getItem(QUALITY_PREFERENCE_KEY);
                //     if (size_preference) {
                //         const res = parseInt(size_preference);
                //         if (!isNaN(res))
                //             return res;
                //     }
                // }

                let startingQuality = 360;
                switch (extData.embedType) {
                    case CP_EmbedType.VIDEO_TINT:
                    case CP_EmbedType.FANPLAYER:
                        startingQuality = 240;
                        break;
                    case CP_EmbedType.VIDEO_TESTO:
                        startingQuality = wvDetect.device?.other ? 480 : 360;
                        break;
                }
                return startingQuality;
            };

            factory.setSizePreferencePolicy(createSizePreference({
                size: useSize(),
                fallback: "lower",
                dimension: "significant"
            }));

            return factory;

        }

        const playerOptions: MegaridePlayerControlsConfig = {
            events: {
                enable: true,
                dynamic_viewable_impression: true,
                dynamic_viewable_impression_cap: 5,
                progress_resolution: 1
            },
            autoplay: config.embeddablePlayerConfig?.autoplayPolicy || MediaAutoplayPolicy.AUTOPLAY_ALWAYS,
            loopPlaylist: config.embeddablePlayerConfig?.loopPlaylist || false,
            usePreferenceQuality: false, //config.embeddablePlayerConfig?.usePreferenceQuality || false,
            audioOnPolicy: config.embeddablePlayerConfig.audioOnPolicy
        }




        if (config.embeddablePlayerConfig.withoutAdvertising) {
            console.log("Without Ads - not downloading IMA");
        }
        else console.log("Downloading IMA");
        //const adsController: AdsController = new NullAdsController();

        const adControllerPromise = config.embeddablePlayerConfig.withoutAdvertising ? Promise.resolve(new NullAdsController()) : (ImaControllerBuilder((ima, gdpr) => {

            /// #13802 vastadunitsconfig 
            const newAdUnitsConfig = buildVastAdUnitsConfig(playlist.elements, {
                embedBehaviour: config.embeddablePlayerConfig.embedBehaviour,
                embedType: extData.embedType,
                site: contentSpecificAdData.site,
                withoutAdvertising: config.embeddablePlayerConfig.withoutAdvertising || false
            })
            const baseOpts: MultipleVASTImaAdCallControllerConfig =
                getAdvConfiguration(useVideoPolicy, extData.embedType, wvDetect, true, newAdUnitsConfig);


            const prebid = config.prebid?.enable ? PrebidBuilder(window, contentSpecificAdData.site, extData.embedType, userData,
                config.prebid?.amazon ? { pubId: config.prebid?.amazon.pubID, behaviour: AmazonBidBehaviour.CHECK_FOR_EXPORTED_BIDS } : null,
                config.prebid?.ias, { enabled: true }, { enabled: true }, config.prebid?.timeout) : new NullPrebidModule();


            const externalUIElements: HasExternalUIElements = {
                getCloseExternalUIElement: () => {
                    return gui.getCloseExternalUIElement();
                }
            }

            //#12431 revisione unmute policy
            // const imaUnmuter = config.audioOnPolicy != MegarideAudioOnPolicy.ON ?
            //     new IMAAdvancedUnmuter(unmuter, gui.getVideoElement().videoItem, externalUIElements, wvDetect) :
            //     new IMAFollowVideoStatusUnmuter(gui.getVideoElement().videoItem, externalUIElements)
            const imaUnmuter = new IMAAdvancedUnmuter(unmuter, gui.getVideoElement().mediaItem, externalUIElements, wvDetect, imaunmuterconfig);
            const resolver = ResolverBuilder(window, useVideoPolicy, contentSpecificAdData, unmuter, imaUnmuter, extData, userData, false);

            return new MultipleVASTImaAdCallController(ima,
                window,
                gdpr,
                userData,
                prebid,
                baseOpts,
                resolver,
                gui.getAdContainer(),
                gui.getVideoElement().mediaItem,
                imaUnmuter
            )
        }, false))

        const controlsPromise = Promise.resolve(new MegaridePlayerControlsBaseImpl(
            PLAYERNAME, cPlaylist, factoryBuilder, adControllerPromise, gui, playerOptions, unmuter, window))

        return controlsPromise.then(ctrl => {
            stats.forEach(s => s.attachEventSource(ctrl));
            return maybeComscorePromise.then(_ => {
                return new ParthenopePlayerV3(ctrl, gui, config, window)
            })
        });

    }


    private constructor(private readonly controls: MegaridePlayerControls,
        private readonly gui: ParthenopeGUIV2,
        private readonly config: ParthenopePlayerConfig,
        private window: Window
    ) {


        dispatchMegarideEvents(this, window, false)

    }

    get headEl(): HTMLElement {
        return this.gui.headEl;
    }

    get guid(): string {
        return this.gui.rootEl?.id;
    }

    private getInitialVolume(): number {
        const configVolume = this.config?.embeddablePlayerConfig?.volume;
        let initalVolume = 0.5;
        if (isFinite(configVolume) && configVolume >= 0) {
            initalVolume = configVolume / 100;
        }
        return initalVolume
    }

    playNext(forceResume: boolean): Promise<void> {
        return this.controls.playNext(forceResume).then();
    }

    subscribeMediaStatus(fn: (s: MediaState) => void): MediaStatusSubscription {
        return this.controls.subscribeMediaStatus(fn)
    }

    getControls(): MegaridePlayerControls {
        return this.controls;
    }
    getGUI(): ParthenopeGUIV2 {
        return this.gui;
    }
    getPlayerName(): string {
        return this.name;
    }
    init(): Promise<void> {
        return this.controls.init().then(ctx => {
            populateAuthorPlaceholder(ctx.ref.info, this.config.embeddablePlayerConfig?.authorPlaceholder, this.window)
            populateViewsPlaceholder(ctx.ref.info, this.config.embeddablePlayerConfig?.viewsPlaceholder, this.window)

            ctx.volume(this.getInitialVolume());
        });
    }

    start(): Promise<void> {
        return this.controls.start().then();
    }
    anchorageUpdate(anchored: boolean): void {
        this.gui.anchorageUpdate(anchored)
    }

    setExternalCloseUIElement(el: ExternalCloseUIElement): void {
        this.gui.setExternalCloseUIElement(el);
    }

    setFullscreen(fullscreen: boolean): void {
        this.gui.setFullscreen(fullscreen);
    }

}
